const CAPTION = 'Match results! | Powered by sportscontrolhub.com'

// Canvas dimensions: Instagram post 4:5
const CANVAS_W = 1080
const CANVAS_H = 1350
const TEMPLATE_SIZE = 1080
const PAD_TOP = Math.floor((CANVAS_H - TEMPLATE_SIZE) / 2) // 135

/**
 * Load the template image as an HTMLImageElement
 */
function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = src
  })
}

/**
 * Wait for Permanent Marker font to be ready (with timeout)
 */
async function ensureFont() {
  try {
    await Promise.race([
      document.fonts.load('bold 80px "Permanent Marker"'),
      new Promise((_, reject) => setTimeout(() => reject(new Error('Font timeout')), 2000)),
    ])
  } catch {
    // Fall back to Impact/system font if Permanent Marker fails
  }
}

/**
 * Extract first name from a player name string or object
 */
function firstName(name) {
  if (!name) return ''
  const str = typeof name === 'object' ? (name.firstName || name.name || '') : String(name)
  return str.split(' ')[0]
}

/**
 * Generate a match result image on a Canvas and return it as a Blob.
 *
 * @param {object} opts
 * @param {string} opts.team1Name
 * @param {string} opts.team2Name
 * @param {number} opts.team1Score
 * @param {number} opts.team2Score
 * @param {string} opts.winnerId
 * @param {string} opts.team1Id
 * @param {string} opts.team2Id
 * @param {string} opts.stageName
 * @param {string} opts.tournamentName
 * @returns {Promise<Blob>}
 */
export async function generateMatchImage(opts) {
  const {
    team1Name, team2Name,
    team1Score, team2Score,
    winnerId, team1Id, team2Id,
    stageName, tournamentName,
  } = opts

  const [template] = await Promise.all([loadImage('/template1.png'), ensureFont()])

  const canvas = document.createElement('canvas')
  canvas.width = CANVAS_W
  canvas.height = CANVAS_H
  const ctx = canvas.getContext('2d')

  // --- Fill extended areas by stretching top/bottom edge pixels ---
  // Use naturalWidth/naturalHeight as source rect to prevent mobile DPI scaling
  const sw = template.naturalWidth
  const sh = template.naturalHeight
  // Top strip: sample 1px row from template top
  ctx.drawImage(template, 0, 0, sw, 1, 0, 0, CANVAS_W, PAD_TOP)
  // Bottom strip: sample 1px row from template bottom
  ctx.drawImage(template, 0, sh - 1, sw, 1, 0, PAD_TOP + TEMPLATE_SIZE, CANVAS_W, CANVAS_H - PAD_TOP - TEMPLATE_SIZE)
  // Draw the template centered — explicit source rect locks to actual pixels
  ctx.drawImage(template, 0, 0, sw, sh, 0, PAD_TOP, TEMPLATE_SIZE, TEMPLATE_SIZE)

  // --- Dynamic text ---
  const centerX = CANVAS_W / 2
  const team1Won = winnerId === team1Id
  const team2Won = winnerId === team2Id

  // Tournament name + stage (top area, angled to match FINAL SCORE)
  ctx.save()
  ctx.translate(centerX, PAD_TOP + 115)
  ctx.rotate(-5 * Math.PI / 180)
  ctx.font = 'bold 34px system-ui, -apple-system, sans-serif'
  ctx.fillStyle = '#000000'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'
  const headerText = stageName ? `${tournamentName}  •  ${stageName}` : tournamentName
  ctx.fillText(headerText, 0, 0)
  ctx.restore()

  // Scores (center of black band) — Permanent Marker
  const scoreY = PAD_TOP + 600
  ctx.font = 'bold 240px "Permanent Marker", Impact, sans-serif'
  ctx.textAlign = 'center'
  ctx.textBaseline = 'middle'

  // Team 1 score (left)
  ctx.fillStyle = '#FFFFFF'
  ctx.fillText(String(team1Score), centerX - 200, scoreY)

  // Dash separator
  ctx.font = 'bold 144px "Permanent Marker", Impact, sans-serif'
  ctx.fillText('-', centerX, scoreY)

  // Team 2 score (right)
  ctx.font = 'bold 240px "Permanent Marker", Impact, sans-serif'
  ctx.fillText(String(team2Score), centerX + 180, scoreY)

  // WIN / LOSS labels
  const labelY = scoreY + 155
  ctx.font = 'bold 40px system-ui, -apple-system, sans-serif'
  ctx.textBaseline = 'middle'

  ctx.fillStyle = team1Won ? '#22c55e' : '#ef4444'
  ctx.fillText(team1Won ? 'WIN' : 'LOSS', centerX - 180, labelY)

  ctx.fillStyle = team2Won ? '#22c55e' : '#ef4444'
  ctx.fillText(team2Won ? 'WIN' : 'LOSS', centerX + 180, labelY)

  // Player first names
  const nameY = labelY + 55
  ctx.font = 'bold 30px "Permanent Marker", Impact, sans-serif'
  ctx.fillStyle = '#FFFFFF'

  const t1Names = team1Name.split('/').map(n => firstName(n.trim())).join(' & ')
  const t2Names = team2Name.split('/').map(n => firstName(n.trim())).join(' & ')

  ctx.fillText(t1Names, centerX - 180, nameY)
  ctx.fillText(t2Names, centerX + 180, nameY)

  // Convert to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(blob => {
      if (blob) resolve(blob)
      else reject(new Error('Failed to generate image'))
    }, 'image/png')
  })
}

/**
 * Fetch a pre-generated server-side match card image and share/download it.
 * Uses the same share/download flow as shareMatchImage.
 *
 * @param {string} url - Public URL of the server-generated PNG
 * @param {string} matchId - Used for filename
 * @returns {Promise<'shared'|'downloaded'|'failed'>}
 */
export async function shareServerMatchImage(url, matchId) {
  const res = await fetch(url)
  if (!res.ok) throw new Error(`Failed to fetch match card image: ${res.status}`)
  const blob = await res.blob()
  return shareMatchImage(blob, matchId)
}

/**
 * Share the generated match image.
 * Uses Web Share API (image file) → download fallback.
 *
 * @param {Blob} blob - The PNG blob
 * @param {string} matchId - Used for filename
 * @returns {Promise<'shared'|'downloaded'|'failed'>}
 */
export async function shareMatchImage(blob, matchId) {
  const file = new File([blob], `match-result-${matchId.substring(0, 7)}.png`, { type: 'image/png' })

  // Try Web Share API with image
  if (navigator.canShare) {
    try {
      const shareData = { files: [file], text: CAPTION }
      if (navigator.canShare(shareData)) {
        await navigator.share(shareData)
        return 'shared'
      }
    } catch (e) {
      if (e.name === 'AbortError') return 'failed'
    }
  }

  // Fallback: download the image
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = file.name
  a.click()
  URL.revokeObjectURL(url)
  return 'downloaded'
}
